/**
 * Расписание: типы, запросы, мутации.
 *
 * Задача живёт в локальной дате семьи (`date` + `time_start` строками),
 * а не в UTC-моменте — так «английский в 17:00» остаётся в 17:00 и после
 * перевода часов, и на телефоне, где выставлен другой пояс.
 */

import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'

import { ApiError, api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queries'

export type TaskKind = 'pickup' | 'meal' | 'activity' | 'dropoff' | 'custom'
export type TaskStatus = 'planned' | 'done' | 'skipped'

export interface ScheduleTask {
  id: number
  date: string
  time_start: string
  time_end?: string | null
  kind: TaskKind
  /** Нужен уведомлениям бота, где картинку не показать. Интерфейс рисует Icon. */
  emoji: string
  title: string
  place?: string | null
  note?: string | null
  reminder_min?: number | null
  status: TaskStatus
  child_id?: number | null
  series_id?: number | null
  done_at?: string | null
  done_event_id?: number | null
  skip_reason?: string | null
  is_overdue: boolean
}

export interface TaskSeries {
  id: number
  weekdays: number[]
  time_start: string
  time_end?: string | null
  kind: TaskKind
  /** Как и у задачи — только для бота. */
  emoji: string
  title: string
  place?: string | null
  reminder_min?: number | null
  child_id?: number | null
  active_from: string
  active_to?: string | null
  paused: boolean
}

export interface ScheduleData {
  start: string
  end: string
  timezone: string
  tasks: ScheduleTask[]
  series: TaskSeries[]
}

export interface DayPlan {
  date: string
  child_name?: string | null
  tasks: ScheduleTask[]
  special_notes: string[]
  acknowledged: boolean
  acknowledged_at?: string | null
}

// Значка здесь нет: он один и тот же для вида задачи в любом месте интерфейса,
// и хранится он в `taskIcon(value)` — иначе список пришлось бы править дважды.
export const KIND_OPTIONS: { value: TaskKind; label: string }[] = [
  { value: 'pickup', label: 'Забрать' },
  { value: 'activity', label: 'Кружок' },
  { value: 'meal', label: 'Еда' },
  { value: 'dropoff', label: 'Домой' },
  { value: 'custom', label: 'Другое' },
]

export const WEEKDAY_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

/* ─── Запросы ───────────────────────────────────────────────────────────── */

export function useSchedule(familyId: number, start?: string, days = 7) {
  return useQuery<ScheduleData, ApiError>({
    queryKey: [...queryKeys.tasks(familyId, start ?? 'now', String(days))],
    queryFn: () => api.get<ScheduleData>('/schedule', { query: { start, days } }),
    staleTime: 30_000,
  })
}

export function useDayPlan(familyId: number, date?: string) {
  return useQuery<DayPlan, ApiError>({
    queryKey: ['plan', familyId, date ?? 'today'],
    queryFn: () => api.get<DayPlan>('/plan', { query: { date } }),
    staleTime: 60_000,
  })
}

/* ─── Мутации ───────────────────────────────────────────────────────────── */

function useInvalidateSchedule(familyId: number) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['tasks', familyId] })
    void queryClient.invalidateQueries({ queryKey: ['plan', familyId] })
    void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
  }
}

export interface TaskInput {
  date: string
  time_start: string
  kind: TaskKind
  title: string
  time_end?: string
  place?: string
  note?: string
  reminder_min?: number | null
  child_id?: number
}

export function useCreateTask(
  familyId: number,
  options?: UseMutationOptions<ScheduleTask, ApiError, TaskInput>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<ScheduleTask, ApiError, TaskInput>({
    mutationFn: (input) => api.post<ScheduleTask>('/tasks', input),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useDeleteTask(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, number>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useCompleteTask(
  familyId: number,
  options?: UseMutationOptions<ScheduleTask, ApiError, number>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<ScheduleTask, ApiError, number>({
    mutationFn: (id) => api.post<ScheduleTask>(`/tasks/${id}/done`),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useSkipTask(
  familyId: number,
  options?: UseMutationOptions<ScheduleTask, ApiError, { id: number; reason: string }>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<ScheduleTask, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => api.post<ScheduleTask>(`/tasks/${id}/skip`, { reason }),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface SeriesInput {
  weekdays: number[]
  time_start: string
  kind: TaskKind
  title: string
  place?: string
  reminder_min?: number | null
  child_id?: number
}

export function useCreateSeries(
  familyId: number,
  options?: UseMutationOptions<TaskSeries, ApiError, SeriesInput>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<TaskSeries, ApiError, SeriesInput>({
    mutationFn: (input) => api.post<TaskSeries>('/series', input),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useArchiveSeries(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, number>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`/series/${id}`),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useAckPlan(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, void>,
) {
  const invalidate = useInvalidateSchedule(familyId)
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => api.post('/plan/ack'),
    onSuccess: (...args) => {
      invalidate()
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}
